import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { debounce } from "lodash";
import { useIntl } from "react-intl";
import performRequest from "network/perform-request";
import { getOrganizationMembersRoute } from "network/api-routes";
import { historySummaryItems, memberOptionLabel } from "./memberLifecycleUtils";

const MemberMergeDialog = ({ source, organizationId, impact, open, submitting, onClose, onConfirm }) => {
  const intl = useIntl();
  const [target, setTarget] = useState(null);
  const [options, setOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [searchError, setSearchError] = useState("");

  const suggestedTargets = useMemo(() => impact?.suggested_merge_targets || [], [impact]);
  const historyItems = historySummaryItems(impact?.history);

  const searchMembers = useMemo(
    () =>
      debounce(async (query) => {
        if (!organizationId || !source?.id) return;

        setLoadingOptions(true);
        setSearchError("");
        try {
          const response = await performRequest("GET", getOrganizationMembersRoute, {
            organization_id: organizationId,
            query,
            per_page: 10,
          });
          setOptions((response.data.data || []).filter((member) => member.id !== source.id));
        } catch (error) {
          setOptions([]);
          setSearchError(
            error?.response?.data?.message || intl.formatMessage({ id: "members.lifecycle.searchMembersError" }),
          );
        } finally {
          setLoadingOptions(false);
        }
      }, 300),
    [intl, organizationId, source?.id],
  );

  useEffect(() => () => searchMembers.cancel(), [searchMembers]);

  useEffect(() => {
    if (!open) return;

    setTarget(null);
    setSearchError("");
    setOptions(suggestedTargets);

    if (suggestedTargets.length === 0 && source?.name) {
      searchMembers(source.name);
    }
  }, [open, searchMembers, source?.name, suggestedTargets]);

  return (
    <Dialog open={open} onClose={() => !submitting && onClose()} fullWidth maxWidth="sm">
      <DialogTitle>{intl.formatMessage({ id: "members.lifecycle.mergeTitle" })}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} mt={0.5}>
          <Alert severity="warning">
            {intl.formatMessage(
              { id: "members.lifecycle.mergeWarning" },
              { sourceName: source?.name, strong: (chunks) => <strong>{chunks}</strong> },
            )}
          </Alert>

          <Autocomplete
            options={options}
            value={target}
            loading={loadingOptions}
            filterOptions={(availableOptions) => availableOptions}
            getOptionLabel={(member) => memberOptionLabel(member, intl.formatMessage)}
            getOptionDisabled={(option) =>
              Boolean(source?.user_id && option.user_id && source.user_id !== option.user_id)
            }
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(_, value) => setTarget(value)}
            onInputChange={(_, value, reason) => {
              if (reason === "input" && value.trim().length >= 2) searchMembers(value.trim());
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={intl.formatMessage({ id: "members.lifecycle.targetMember" })}
                helperText={intl.formatMessage({ id: "members.lifecycle.targetMemberHelp" })}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingOptions && <CircularProgress color="inherit" size={18} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          {searchError && <Alert severity="error">{searchError}</Alert>}

          {target && (
            <Alert severity="info">
              <Typography variant="body2">
                {intl.formatMessage(
                  { id: "members.lifecycle.targetExplanation" },
                  { targetName: target.name, strong: (chunks) => <strong>{chunks}</strong> },
                )}
              </Typography>
            </Alert>
          )}

          {(impact?.manual_points?.count > 0 || historyItems.length > 0) && (
            <DialogContentText>
              {intl.formatMessage(
                {
                  id:
                    historyItems.length > 0
                      ? "members.lifecycle.mergeSummaryWithHistory"
                      : "members.lifecycle.mergeSummary",
                },
                {
                  manualCount: impact?.manual_points?.count || 0,
                  historyCount: historyItems.reduce((total, item) => total + item.count, 0),
                },
              )}
            </DialogContentText>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          {intl.formatMessage({ id: "Cancelar" })}
        </Button>
        <Button variant="contained" onClick={() => onConfirm(target)} disabled={!target || submitting}>
          {intl.formatMessage({
            id: submitting ? "members.lifecycle.merging" : "members.lifecycle.mergeAndKeepHistory",
          })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MemberMergeDialog;
